/**
 * Mobile Tab Manager
 * 
 * Handles tab switching functionality for mobile devices
 * Switches between input and output panels using tabs
 */

export class MobileTabManager {
  constructor() {
    this.activePanel = 'input'
    this.tabs = null
    this.panels = null
    this.init()
  }

  init() {
    // Only initialize on mobile screens
    if (!this.isMobile()) {
      return
    }

    this.setupElements()
    this.setupEventListeners()
    this.setActivePanel(this.activePanel, false) // Set initial state without animation
  }

  setupElements() {
    this.tabs = document.querySelectorAll('.mobile-tab')
    this.panels = {
      input: document.querySelector('.input-panel'),
      output: document.querySelector('.output-panel')
    }

    // Add initial active class to input panel
    if (this.panels.input) {
      this.panels.input.classList.add('mobile-panel-active')
    }
  }

  setupEventListeners() {
    // Tab click handlers
    this.tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const panel = e.currentTarget.getAttribute('data-panel')
        this.setActivePanel(panel)
      })
    })

    // Handle window resize to reinitialize if needed
    window.addEventListener('resize', () => {
      if (this.isMobile()) {
        this.init()
      } else {
        this.cleanup()
      }
    })

    // Handle orientation change on mobile devices
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        if (this.isMobile()) {
          this.init()
        } else {
          this.cleanup()
        }
      }, 100)
    })
  }

  setActivePanel(panelName, animate = true) {
    if (!this.isMobile() || !this.panels[panelName]) {
      return
    }

    const previousPanel = this.activePanel
    this.activePanel = panelName

    // Update tab states
    this.tabs.forEach(tab => {
      const tabPanel = tab.getAttribute('data-panel')
      const isActive = tabPanel === panelName

      tab.classList.toggle('mobile-tab-active', isActive)
      tab.setAttribute('aria-pressed', isActive.toString())
    })

    // Update panel visibility
    Object.keys(this.panels).forEach(key => {
      const panel = this.panels[key]
      const isActive = key === panelName

      if (animate && panel) {
        // Add animation class
        panel.style.transition = 'opacity 0.2s ease, transform 0.2s ease'

        if (isActive) {
          panel.classList.add('mobile-panel-active')
          panel.style.opacity = '0'
          panel.style.transform = 'translateX(10px)'

          // Animate in
          requestAnimationFrame(() => {
            panel.style.opacity = '1'
            panel.style.transform = 'translateX(0)'
          })
        } else {
          panel.style.opacity = '0'
          panel.style.transform = 'translateX(-10px)'

          setTimeout(() => {
            panel.classList.remove('mobile-panel-active')
          }, 200)
        }
      } else {
        // No animation
        panel.classList.toggle('mobile-panel-active', isActive)
      }
    })

    // Trigger custom event for other components to listen to
    window.dispatchEvent(new CustomEvent('mobilePanelChanged', {
      detail: {
        activePanel: panelName,
        previousPanel: previousPanel
      }
    }))

    console.log(`Mobile panel switched to: ${panelName}`)
  }

  getActivePanel() {
    return this.activePanel
  }

  isMobile() {
    return window.innerWidth <= 768
  }

  cleanup() {
    // Remove mobile-specific classes when switching to desktop
    if (this.panels) {
      Object.values(this.panels).forEach(panel => {
        if (panel) {
          panel.classList.remove('mobile-panel-active')
          panel.style.transition = ''
          panel.style.opacity = ''
          panel.style.transform = ''
        }
      })
    }
  }

  // Public method to programmatically switch tabs
  switchToPanel(panelName) {
    if (['input', 'output'].includes(panelName)) {
      this.setActivePanel(panelName)
    }
  }

}
